import { ErrorHandler, inject } from '@angular/core';
import { Router, UrlTree } from '@angular/router';
import { selectAccountMembershipCountState } from '@mkp/account-membership/state';
import { AppConfig } from '@mkp/shared/util-app-config';
import {
  GUARD_ROUND_TRIP_TIMEOUT_MS,
  GuardTimeoutError,
  errorUrlTree,
} from '@mkp/shared/util-guards';
import { selectIsBoUser } from '@mkp/user/state-logged-in/selectors';
import { Store } from '@ngrx/store';
import { combineLatest, filter, map, Observable, of, take, timeout } from 'rxjs';

/**
 * Gates `/select-workspace` on the user actually having a workspace to choose between.
 *
 * The membership count is `null` until the bootstrap request lands, and a `canMatch` guard that
 * never emits stalls recognition with no NavigationEnd, no error and no telemetry — the splash
 * screen simply stays up. So this waits for the fetch to *settle* rather than for a non-null
 * count, which also terminates when the fetch fails, and carries a deadline as a backstop.
 *
 * A BO user is answered without waiting at all: they pick an account on the BO dashboard, not
 * here.
 */
export function hasMultipleWorkspacesGuard(): Observable<boolean | UrlTree> {
  const store = inject(Store);
  const router = inject(Router);
  const errorHandler = inject(ErrorHandler);

  // Plain `/dashboard`, not workspace-prefixed: this guard only runs when no workspace is
  // selected, so WorkspaceRouteService has nothing to build a prefix from. `/dashboard` carries
  // its own guards, which route BO and non-BO users on from there.
  const dashboard = () => router.parseUrl(AppConfig.routes.dashboard);

  // The count and its settled flag come from one selector on purpose: as two `store.select`
  // streams, combineLatest emits a half-updated pair where the fetch already reads as settled
  // but the count is still the previous null, which sends a multi-workspace user to /dashboard.
  return combineLatest([
    store.select(selectIsBoUser),
    store.select(selectAccountMembershipCountState),
  ]).pipe(
    // `isBoUser` is null until known, which counts as not-BO here, matching every other consumer.
    filter(([isBoUser, { settled }]) => !!isBoUser || settled),
    take(1),
    map(([isBoUser, { count }]) => (!isBoUser && (count ?? 0) > 1) || dashboard()),
    timeout({
      first: GUARD_ROUND_TRIP_TIMEOUT_MS,
      with: () => {
        errorHandler.handleError(
          new GuardTimeoutError(
            'hasMultipleWorkspacesGuard',
            getAttemptedUrl(router),
            GUARD_ROUND_TRIP_TIMEOUT_MS
          )
        );
        return of(errorUrlTree(router));
      },
    })
  );
}

const getAttemptedUrl = (router: Router): string => {
  const navigation = router.getCurrentNavigation();
  const attemptedUrl = navigation?.finalUrl ?? navigation?.extractedUrl;

  return attemptedUrl ? attemptedUrl.toString() : router.url;
};
